<template>
  <MaintenancePage v-if="showMaintenancePage" />
  <template v-else>
    <AppBanner />
    <RouterView />
  </template>
  <LoadingScreen v-if="isLoading" />
</template>

<script lang="ts">
import { useAuth0 } from '@auth0/auth0-vue'
import { getLogger } from '@thyme/libs/src/logging/logger'
import { storeToRefs } from 'pinia'
import { defineComponent, watch, onMounted, onBeforeUnmount } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import { useAppLoadStore } from '@/libs/appLoadStore'
import { getAppConfig } from '@/libs/config/config'
import { setDatadogUser } from '@/libs/datadog/datadog'
import { useFlagStore } from '@/libs/flags'
import MaintenancePage from '@/pages/MaintenancePage/MaintenancePage.vue'
import AppBanner from '@/tokens/AppBanner/AppBanner.vue'
import LoadingScreen from '@/tokens/LoadingScreen/LoadingScreen.vue'
import { storeExternalUserId } from './libs/events/externalUser'
import { storeUTMParams } from './libs/events/utm'
import {
  ENTITY_ID_CLAIM,
  RECOVERABLE_AUTH_ERRORS,
} from './libs/state/auth0Client'

export default defineComponent({
  components: {
    RouterView,
    AppBanner,
    LoadingScreen,
    MaintenancePage,
  },
  setup() {
    const auth0 = useAuth0()
    const router = useRouter()
    const { isLoading } = storeToRefs(useAppLoadStore())
    const { endLoading } = useAppLoadStore()
    const { showMaintenancePage } = storeToRefs(useFlagStore())
    const config = getAppConfig()

    storeUTMParams()
    storeExternalUserId()

    watch(
      [auth0.isAuthenticated, auth0.isLoading, auth0.user],
      ([isAuthenticated, isLoading, user]) => {
        if (!isAuthenticated || isLoading || !user) {
          return
        }

        const entityId = user?.[ENTITY_ID_CLAIM]
        const email = user?.email
        setDatadogUser(config, { id: entityId, email })
      },
      { immediate: true }
    )

    watch(
      auth0.error,
      async (error) => {
        if (error) {
          // Unauthenticated routes don't require an Auth0 session;
          // suppress error redirects so their guards (identityGuard, etc.) can run.
          if (router.currentRoute.value.meta.unauthenticated) {
            return
          }

          const isRecoverable = RECOVERABLE_AUTH_ERRORS.some((msg) =>
            error.message?.includes(msg)
          )
          if (isRecoverable) {
            getLogger().info('Auth0 session expired, redirecting to login', {
              error,
            })
            await router.push({
              name: '#loginMethod',
              query: { redirect: router.currentRoute.value.fullPath },
            })
          } else {
            getLogger().error('Auth0 error occurred', { error: error })
            await router.push({ name: '#error' })
          }
        }
      },
      {
        immediate: true,
      }
    )

    // hide LoadingScreen when page is restored from bfcache
    const handlePageTransition = (event: PageTransitionEvent) => {
      if (event.persisted) {
        endLoading()
      }
    }

    onMounted(() => {
      window.addEventListener('pageshow', handlePageTransition)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('pageshow', handlePageTransition)
    })

    return {
      isLoading,
      showMaintenancePage,
    }
  },
})
</script>
